import { dates, dom } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { CellModel, ColumnSource, RequestFilters, RowSource } from "@omegagrid/grid";
import rp, { GridEditorAdapter, GridEditorOptions } from "@reporting/rp-app-host";
import { Timesheet, TimesheetDay } from "@reporting/rp-types/users";

const PAGE_SIZE = 500;

export type TimesheetMode = 'daily' | 'segments';

export const TIMESHEET_MODES: { k: TimesheetMode, v: () => string }[] = [
	{ k: 'daily', v: () => msg('Daily totals') },
	{ k: 'segments', v: () => msg('Segments') },
];

/** 3725 → "1:02:05" */
export const formatDuration = (seconds: number | null | undefined) => {
	if (seconds == null) return '';
	const total = Math.max(0, Math.round(seconds));
	const h = Math.floor(total / 3600);
	const m = Math.floor((total % 3600) / 60);
	const s = total % 60;
	return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * Time super-admins spent in companies (P_TIMESHEET, written by ActivityMiddleware in rp-ms-core
 * from their requests). 'daily' sums the segments of a user in a company per day, 'segments' lists
 * the raw segments (a new one starts after 5 minutes without a request).
 */
export class TimesheetGridAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];

	/** @param userName narrows the timesheet to one super-admin (null = everyone) */
	constructor(readonly mode: TimesheetMode = 'daily', readonly userName: string | null = null) { super() }

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;

		const who: ColumnSource[] = [{
			data: 'login_name',
			header: msg('User'),
			t: 't',
			w: 150,
		}, {
			data: 'user_name',
			header: msg('User name'),
			t: 't',
			hidden: true,
		}, {
			data: 'group_name',
			header: msg('Group'),
			t: 't',
			w: 150,
		}, {
			data: 'accounting',
			header: '#',
			t: 'n',
			w: 85,
		}, {
			data: 'company',
			header: msg('Company'),
			t: 't',
			w: 180,
		}];

		const duration: ColumnSource = {
			data: 'seconds',
			header: msg('Time'),
			t: 'n',
			w: 75,
			renderer: this.durationRenderer,
		};

		this._columns = this.mode === 'daily' ? [{
			data: 'date',
			header: msg('Date'),
			t: 'd',
			z: dates.getDateFormat(), // a day, without the time part the editor's default date format carries
			w: 85,
		}, ...who, duration, {
			data: 'segments',
			header: msg('Segments'),
			t: 'n',
			w: 90,
		}, {
			data: 'date_start',
			header: msg('First activity'),
			t: 'dt',
			w: 145,
		}, {
			data: 'date_end',
			header: msg('Last activity'),
			t: 'dt',
			w: 145,
		}] : [{
			data: 'date_start',
			header: msg('From'),
			t: 'dt',
			w: 145,
		}, {
			data: 'date_end',
			header: msg('To'),
			t: 'dt',
			w: 145,
		}, duration, ...who, {
			data: 'service',
			header: msg('Service'),
			t: 't',
			w: 100,
		}];
		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const path = this.mode === 'daily' ? 'timesheet/daily' : 'timesheet';
		// the user filter travels outside `where`, which the grid's own column filters replace as a whole
		const params = { ...this.createRequestParams(filters, { count: PAGE_SIZE }), user: this.userName ?? undefined };
		const rows = await rp.ms('users').get<(Timesheet | TimesheetDay)[]>(path, params);
		if (this.mode !== 'daily') return rows.map(data => ({ data }));
		// the day arrives as "yyyy-MM-dd 00:00:00.000000"; a 'd' cell parses the bare ISO day as local
		// midnight, whereas the full string would be taken as UTC and shift by the timezone offset
		return rows.map((data: TimesheetDay) => ({ data: { ...data, date: data.date?.slice(0, 10) } }));
	}

	durationRenderer = (div: HTMLDivElement, cell: CellModel) => {
		dom.empty(div);
		div.textContent = formatDuration((cell.row.data as Timesheet).seconds);
		return div;
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: true,
			pageSize: PAGE_SIZE,
			editable: false,
			lightTheme: null,
			darkTheme: null,
			toolbar: {
				save: { enabled: false },
				discard: { enabled: false },
				new: { enabled: false },
			},
		});
	}

}
